import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Check, Maximize2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { demoImage, productTours, type ProductTourId } from './product-tours';

export function ProductTour() {
    const { t } = useTranslation('product-tour');
    const [selected, setSelected] = useState<ProductTourId>('resident');
    const tour = productTours.find((item) => item.id === selected)!;
    const prefix = `features.${selected}`;
    const chooseTour = (id: ProductTourId) => {
        setSelected(id);
        window.history.replaceState(window.history.state, '', `#demo-${id}`);
    };

    useEffect(() => {
        const followHash = () => {
            const id = window.location.hash.replace('#demo-', '');
            const target = productTours.find((item) => item.id === id);
            if (target) {
                setSelected(target.id);
                document.getElementById('how-it-works')?.scrollIntoView();
            }
        };
        followHash();
        window.addEventListener('hashchange', followHash);
        return () => window.removeEventListener('hashchange', followHash);
    }, []);

    return (
        <section id="how-it-works" className="marketing-tour-section" aria-labelledby="tour-heading">
            <div className="marketing-container py-16 sm:py-20">
                <div className="mb-8 max-w-2xl">
                    <p className="marketing-eyebrow">{t('eyebrow')}</p>
                    <h2 id="tour-heading" className="marketing-section-title mt-4">
                        {t('heading')}
                    </h2>
                </div>
                <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" role="group" aria-label={t('chooseFeature')}>
                    {productTours.map(({ id, icon: Icon }) => (
                        <button
                            key={id}
                            type="button"
                            aria-pressed={selected === id}
                            aria-controls="product-tour-content"
                            onClick={() => chooseTour(id)}
                            className={cn('marketing-tour-choice', selected === id && 'is-selected')}
                        >
                            <Icon className="size-5 shrink-0" aria-hidden="true" />
                            <span>{t(`features.${id}.label`)}</span>
                        </button>
                    ))}
                </div>
                <div id="product-tour-content" className="mt-5 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                    <div className="grid items-center lg:grid-cols-[0.75fr_1.55fr]">
                        <div className="min-w-0 p-6 sm:p-8">
                            <h3 className="text-2xl leading-tight font-semibold tracking-tight sm:text-3xl">{t(`${prefix}.title`)}</h3>
                            <p className="mt-4 leading-7 text-muted-foreground">{t(`${prefix}.description`)}</p>
                            <ul className="mt-6 space-y-3">
                                {[0, 1, 2].map((index) => (
                                    <li key={index} className="flex items-start gap-2.5 text-sm leading-6">
                                        <Check className="mt-1 size-4 shrink-0 text-primary" aria-hidden="true" />
                                        <span>{t(`${prefix}.points.${index}`)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <figure className="min-w-0 border-t border-border bg-muted/50 p-3 sm:p-5 lg:border-t-0 lg:border-l">
                            <Dialog>
                                <DialogTrigger asChild>
                                    <button type="button" className="marketing-screenshot-button group" aria-label={t('viewScreenshot')}>
                                        <img
                                            key={tour.shot}
                                            src={demoImage(tour.shot)}
                                            alt={t(`${prefix}.alt`)}
                                            width={1440}
                                            height={1000}
                                            loading="lazy"
                                            decoding="async"
                                            className="h-full w-full object-contain"
                                        />
                                        <span className="absolute right-3 bottom-3 inline-flex min-h-11 items-center gap-2 rounded-lg bg-slate-950 px-3 text-xs font-semibold text-white shadow-lg">
                                            <Maximize2 className="size-4" aria-hidden="true" />
                                            {t('viewScreenshot')}
                                        </span>
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="marketing max-h-[95dvh] overflow-y-auto p-3 sm:max-w-[min(96vw,1440px)] sm:p-6 [&>button:last-child]:min-h-11 [&>button:last-child]:min-w-11">
                                    <DialogTitle className="pr-12 text-left leading-7">{t(`${prefix}.title`)}</DialogTitle>
                                    <DialogDescription className="pr-8">{t(`${prefix}.description`)}</DialogDescription>
                                    <img
                                        src={demoImage(tour.shot)}
                                        alt={t(`${prefix}.alt`)}
                                        width={1440}
                                        height={1000}
                                        className="h-auto w-full rounded-lg border border-border"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('sampleData')} · {t('screenshotLanguage')}
                                    </p>
                                </DialogContent>
                            </Dialog>
                            <figcaption className="mt-3 text-center text-xs leading-5 text-muted-foreground">
                                {t('sampleData')} · {t('screenshotLanguage')}
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </div>
        </section>
    );
}
